import * as Sentry from '@sentry/react';

export const initSentry = () => {
  // Check if VITE_SENTRY_DSN is defined
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.warn('Sentry DSN not provided. Error tracking is disabled.');
    return;
  }

  // Initialize Sentry
  Sentry.init({
    dsn: dsn,
    // Set this to true to enable capturing of PII data like usernames and emails
    sendDefaultPii: false,
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions
    replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
    // Enable source maps in the Sentry UI
    integrations: [
      Sentry.browserProfilingIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Enable source map support
    _experiments: {
      // Configure reporting of source maps
      profilesSampleRate: 1.0,
    },
    // Add release information
    release: import.meta.env.VITE_SENTRY_RELEASE || 'development',
    environment: import.meta.env.MODE,
    // Enable debug mode in development
    debug: import.meta.env.MODE === 'development',
    // Capture all errors (don't just sample)
    beforeSend(event) {
      // Add extra debugging info for database errors
      if (event.exception && 
          event.exception.values && 
          event.exception.values[0] && 
          event.exception.values[0].value &&
          (event.exception.values[0].value.includes('profiles.address') || 
           event.exception.values[0].value.includes('coins.'))) {
        event.tags = {
          ...event.tags,
          database_error: true,
          schema_issue: true
        };
      }
      return event;
    }
  });
  
  console.log(`Sentry initialized in ${import.meta.env.MODE} mode with DSN: ${dsn ? '[configured]' : '[missing]'}`);
};

// Helper function to capture exceptions
export const captureException = (error: unknown, context?: Record<string, any>) => {
  console.error('Error captured:', error);
  
  // Make sure error is properly formatted for Sentry
  const errorObj = error instanceof Error ? error : new Error(String(error));
  
  // Add additional context to error
  const user = sessionStorage.getItem('user') ? JSON.parse(sessionStorage.getItem('user') || '{}') : {};
  const sentryContext = {
    ...context,
    timestamp: new Date().toISOString(),
    environment: import.meta.env.MODE,
    // Include current page URL
    url: window.location.href,
    // Include any user data we might have
    user: user.address || 'unknown',
  };
  
  // Send to Sentry with context
  Sentry.captureException(errorObj, { 
    extra: sentryContext 
  });
  
  return errorObj;
};

// Helper function to manually test Sentry
export const testSentryError = () => {
  try {
    throw new Error("This is a test error from Sentry.testError()");
  } catch (error) {
    captureException(error, { source: "manual test" });
    return error;
  }
};

// Create a higher-order function to wrap async functions with Sentry error reporting
export const withErrorReporting = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context: Record<string, any> = {}
): ((...args: Parameters<T>) => Promise<ReturnType<T>>) => {
  return async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    try {
      return await fn(...args);
    } catch (error) {
      // Capture and report the error with additional context
      captureException(error, {
        ...context,
        functionName: fn.name || 'anonymous function',
        arguments: JSON.stringify(args.map(arg => 
          typeof arg === 'object' ? '(complex object)' : arg
        )),
      });
      
      // Re-throw the error to maintain original behavior
      throw error;
    }
  };
};

// Create a React error boundary component for use in the app
export const SentryErrorBoundary = Sentry.ErrorBoundary;
